import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Paperclip } from "lucide-react";

import { MentionTextarea } from "@/components/MentionTextarea";
import { Button } from "@/components/ui/Button";
import { FilePicker } from "@/components/ui/FilePicker";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";

import {
  createFirmwareReleaseNoteComment,
  fetchFirmwareReleaseNoteComments,
  firmwareReleaseNoteCommentsKey,
  type FirmwareReleaseNoteComment,
} from "./firmwareApi";

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function CommentAttachments({ comment }: { comment: FirmwareReleaseNoteComment }): JSX.Element | null {
  if (comment.attachments.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {comment.attachments.map((attachment) => (
        <li key={attachment.id} className="text-sm">
          {attachment.kind === "link" && attachment.linkUrl ? (
            <a
              href={attachment.linkUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sky-700 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {attachment.linkLabel?.trim() || attachment.linkUrl}
            </a>
          ) : attachment.kind === "file" ? (
            <a
              href={`/api/engineering/firmware-release-notes/${String(comment.releaseNoteId)}/comments/attachments/${String(attachment.id)}/file`}
              className="inline-flex items-center gap-1 text-sky-700 hover:underline"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {attachment.fileName ?? "Attachment"}
            </a>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export function FirmwareReleaseNoteCommentsPanel(props: {
  releaseNoteId: number;
  writesAllowed: boolean;
}): JSX.Element {
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [pendingLinks, setPendingLinks] = useState<Array<{ url: string; label: string | null }>>([]);

  const commentsQuery = useQuery({
    queryKey: firmwareReleaseNoteCommentsKey(props.releaseNoteId),
    queryFn: () => fetchFirmwareReleaseNoteComments(props.releaseNoteId),
    enabled: props.releaseNoteId > 0,
  });

  const comments = commentsQuery.data?.comments ?? [];
  const pendingLinkRows = useMemo(() => pendingLinks, [pendingLinks]);

  const createMutation = useMutation({
    mutationFn: () => {
      const links = [...pendingLinks];
      const trimmedLinkUrl = linkUrl.trim();
      if (trimmedLinkUrl !== "") {
        links.push({ url: trimmedLinkUrl, label: linkLabel.trim() || null });
      }
      return createFirmwareReleaseNoteComment(props.releaseNoteId, {
        body: body.trim(),
        links,
        files,
      });
    },
    onSuccess: async () => {
      setBody("");
      setLinkUrl("");
      setLinkLabel("");
      setFiles([]);
      setPendingLinks([]);
      await queryClient.invalidateQueries({ queryKey: firmwareReleaseNoteCommentsKey(props.releaseNoteId) });
      showToast({ tone: "success", message: "Comment saved." });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const canSubmit = body.trim() !== "" || files.length > 0 || pendingLinks.length > 0 || linkUrl.trim() !== "";

  return (
    <div className="space-y-3 border-t border-slate-200 pt-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900">Comments</h4>
        <span className="text-xs text-slate-500">
          {comments.length === 1 ? "1 comment" : `${String(comments.length)} comments`}
        </span>
      </div>

      {commentsQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading comments…
        </p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-600">No comments yet.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <article key={comment.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-slate-800">{comment.body}</p>
              <CommentAttachments comment={comment} />
              <p className="mt-2 text-xs text-slate-500">
                {comment.createdByDisplayName ?? "Unknown"} · {formatTimestamp(comment.createdAt)}
              </p>
            </article>
          ))}
        </div>
      )}

      {props.writesAllowed ? (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
          <div>
            <Label htmlFor={`release-note-comment-${String(props.releaseNoteId)}`}>Add comment</Label>
            <MentionTextarea
              id={`release-note-comment-${String(props.releaseNoteId)}`}
              className="mt-1.5 min-h-[5rem] w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={body}
              onChange={setBody}
              placeholder="Notes for the team. Type @ to mention someone. Attach files or paste a Google Drive link below."
            />
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] md:items-end">
            <div>
              <Label htmlFor={`release-note-link-${String(props.releaseNoteId)}`}>Link URL</Label>
              <Input
                id={`release-note-link-${String(props.releaseNoteId)}`}
                className="mt-1.5"
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://drive.google.com/..."
              />
            </div>
            <div>
              <Label htmlFor={`release-note-link-label-${String(props.releaseNoteId)}`}>Link label</Label>
              <Input
                id={`release-note-link-label-${String(props.releaseNoteId)}`}
                className="mt-1.5"
                value={linkLabel}
                onChange={(event) => setLinkLabel(event.target.value)}
                placeholder="Optional"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              className="md:mb-0.5"
              disabled={linkUrl.trim() === ""}
              onClick={() => {
                const url = linkUrl.trim();
                if (url === "") return;
                setPendingLinks((current) => [...current, { url, label: linkLabel.trim() || null }]);
                setLinkUrl("");
                setLinkLabel("");
              }}
            >
              Add link
            </Button>
          </div>
          {pendingLinkRows.length > 0 ? (
            <ul className="space-y-1 text-sm text-slate-700">
              {pendingLinkRows.map((link, index) => (
                <li key={`${link.url}-${String(index)}`} className="flex items-center justify-between gap-2">
                  <span className="truncate">{link.label ?? link.url}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingLinks((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
          <FilePicker
            id={`release-note-files-${String(props.releaseNoteId)}`}
            label="Attach file"
            onChange={(file) => {
              if (file == null) return;
              setFiles((current) => [...current, file]);
            }}
          />
          {files.length > 0 ? (
            <p className="text-xs text-slate-600">
              {files.length === 1 ? files[0]?.name : `${String(files.length)} files selected`}
            </p>
          ) : null}
          <Button disabled={!canSubmit || createMutation.isPending} onClick={() => createMutation.mutate()}>
            {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save comment
          </Button>
        </div>
      ) : (
        <p className="text-xs text-slate-500">Comments are read-only here. Add comments from the Release notes tab.</p>
      )}
    </div>
  );
}
